import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="16393" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-16 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <nav className="hidden md:flex items-center gap-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="relative text-xs font-bold uppercase tracking-widest text-foreground/80 transition-all duration-300 hover:text-primary py-2 font-sans">
            {item.label}
            <span className="absolute bottom-0 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-[width] duration-300 ease-out group-hover:w-full peer-hover:w-full hover:w-full" />
            {}
            <span className="absolute -bottom-1 left-1/2 h-[1px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 group-hover:w-full pointer-events-none" />
            <style jsx>{`
              a:hover span {
                width: 100%;
              }
            `}</style>
          </Link>
        </div>)}
    </nav>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-32 animate-pulse rounded bg-muted/20" />)}
      </div>;
  }
  return <nav className="flex flex-col items-center justify-center gap-8 p-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="w-full text-center">
          <Link to={item.href || "#"} className="text-3xl font-medium tracking-widest text-foreground hover:text-primary transition-colors font-serif" onClick={onClose}>
            {item.label}
          </Link>
        </div>)}
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-50 w-full transition-all duration-700 dark bg-background text-foreground ${isScrolled ? "bg-background/90 backdrop-blur-xl border-b border-primary/20 shadow-xl shadow-primary/5" : "bg-background border-b border-transparent"}`}>
      <div className="container mx-auto px-10 h-24 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex-shrink-0 z-50 transition-transform duration-500 hover:scale-105">
          <img src="/img/pupzstudio_logo_premium.png" alt="PupzStudio Elite Concierge" className="h-12 w-auto object-contain brightness-110 contrast-125" src="https://pupzstudio.com/wp-content/uploads/2026/03/pupzstudio_logo_footer.png" />
        </Link>

        {}
        <div className="hidden md:block">
          <MenuProvider menu_id="34">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="md:hidden z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-foreground hover:bg-primary/10 hover:text-primary transition-all duration-300" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </Button>
        </div>

        {}
        <div className={`fixed inset-0 z-40 bg-background/98 backdrop-blur-3xl transition-all duration-700 md:hidden flex flex-col items-center justify-center ${isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-10"}`}>
          <MenuProvider menu_id="34">
            <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>
        </div>
      </div>
    </header>;
}